import * as angular from 'angular';
import * as _ from 'lodash';

import { tagsImported } from '../../common/events';
import { ITagDefinitionService, ILoggerService, IRequestService } from '../../services';
import { Alias, ImportedTagDefinition, DataType } from '../../models';

export interface ItagsImportService {
    search(guid: string, searchString: string): angular.IPromise<ImportedTagDefinition[]>;
    getByName(name: string): any;
    import(tagImport: ImportedTagDefinition, tempAliasList: Alias[]): angular.IPromise<number[]>;
    importAll(tempAliasList: Alias[], displayed: ImportedTagDefinition[]): angular.IPromise<number[]>;
    searchResults();
    searchUnknownUnitsFromResults(): angular.IPromise<string[]>;
    searchUnknownUnits(tagImportList: ImportedTagDefinition[]): angular.IPromise<string[]>;
}

export class TagsImportService implements ItagsImportService {
    static serviceId = 'tagsImportService';

    private _searchResults: any[];

    static $inject = [
        '$q',
        '$rootScope',
        'tagDefinitionService',
        'loggerService',
        'requestService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$rootScope: angular.IRootScopeService,
        private _tagDefinitionService: ITagDefinitionService,
        private _loggerService: ILoggerService,
        private _requestService: IRequestService
    ) {
    }

    search(guid: string, searchString: string): angular.IPromise<any> {
        const defered = this._$q.defer();
        const result: any = this.extractPatterns(searchString);
        const isOnlyNew = this.isOnlyNew(searchString);
        const isUnusedRemoved = this.isUnusedRemoved(searchString);
        const searchParameters = {
            connectionPointGuid: guid,
            nameFilter: result['n'] ? result['n'] : '*',
            unitFilter: result['u'] ? result['u'] : '*',
            commentFilter: result['c'] ? result['c'] : '*'
        };
        this._tagDefinitionService
            .search(searchParameters)
            .then(data => {
                if (isUnusedRemoved) {
                    data = data.filter(d => !d.isInUse);
                }
                if (isOnlyNew) {
                    data = data.filter(d => !d.isAlreadyExisting);
                }
                this._searchResults = data;
                defered.resolve(data);
            })
            .catch((reason) => {
                defered.reject(reason);
            });
        return defered.promise;
    }

    getByName(name: string) {
        return _.find(this._searchResults, r => r.name === name);
    }

    import(tagImport: ImportedTagDefinition, tempAliasList: Alias[]): angular.IPromise<number[]> {
        const defered = this._$q.defer<number[]>();
        this._tagDefinitionService
            .importList([tagImport], tempAliasList)
            .then(() => {
                this._loggerService.logSuccess(`Tag ${tagImport.name} successuflly imported.`);
                this._$rootScope.$broadcast(tagsImported, [tagImport]);
                this._requestService.cleanAllCache();
                defered.resolve();
            })
            .catch(_reason => {
                this._loggerService.logSuccess(`Failed to import tag ${tagImport.name}.`);
                defered.reject();
            });
        return defered.promise;
    }

    searchUnknownUnitsFromResults(): angular.IPromise<string[]> {
        return this.searchUnknownUnits(this._searchResults);
    }

    searchUnknownUnits(tagImportList: any[]): angular.IPromise<string[]> {
        const defered = this._$q.defer<string[]>();
        const units = _.uniq(tagImportList
            .filter(d => !d.isAlreadyExisting && d.dataType !== DataType.Digital)
            .map(d => d.unit)
        );
        if (units && units.length > 0) {
            this._tagDefinitionService.findUnknownUnits(units)
                .then(data => {
                    defered.resolve(data);
                })
                .catch(reason => {
                    this._loggerService.logError(`Failed to search unknwown units.`);
                    defered.reject(reason);
                });
        } else {
            defered.resolve([]);
        }

        return defered.promise;
    }

    importAll(tempAliasList: any[], displayed: ImportedTagDefinition[]): angular.IPromise<any> {
        const defered = this._$q.defer();
        const nonExisting = displayed.filter(t => !t.isAlreadyExisting);
        this._tagDefinitionService
            .importList(nonExisting, tempAliasList)
            .then(data => {
                let msg = 'Tags mapped successfully';
                if (data[0] !== undefined) {
                    msg = `${data[0]} tags imported.`;
                }
                this._loggerService.logSuccess(msg);

                this._$rootScope.$broadcast(tagsImported, nonExisting);
                this._requestService.cleanAllCache();
                defered.resolve();
            })
            .catch(reason => {
                this._loggerService.logError(`Failed to import tags.`);
                defered.reject(reason);
            });
        return defered.promise;

    }

    searchResults() {
        return this._searchResults;
    }

    private isOnlyNew(searchString: string): boolean {
        const result = this.extractPatterns(searchString);
        return result['unused'] && result['unused'].indexOf('true') >= 0;
    }

    private isUnusedRemoved(searchString: string): boolean {
        const result = this.extractPatterns(searchString);
        return result && result['ne'] && result['ne'].indexOf('true') >= 0;
    }

    private convertString(input: string): string {
        const result = (input.indexOf('*') < 0 && input.indexOf('?') < 0) ? `*${input}*` : input;
        return result;
    }

    private extractPatterns(searchString: string): any {
        const patterns = searchString.toLowerCase().split(' ');
        const result: any = {};
        patterns.forEach(p => {
            const spli = p.split(':');
            if (spli.length === 1) {
                result['n'] = this.convertString(spli[0]);
            } else {
                result[spli[0]] = this.convertString(spli[1]);
            }
            return result;
        });
        return result;
    }
}